import { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePOAs, type POA } from '../../../hooks/usePOAs'
import { useAuth } from '../../../contexts/AuthContext'
import { scanPOAFolder, getDriveFileContent } from '../../../hooks/useGoogleDrive'
import { parsePOAWithClaude, validateExtractedPOA, type ValidationWarning } from '../../../api/claudePOAParser'
import { supabase } from '../../../api/supabase'

export type { POA }

type POAStatus = 'active' | 'expiring_soon' | 'expired'
type SortCol = 'importer_name' | 'eori' | 'signed_date' | 'agent_name' | 'created_at'
type ViewMode = 'list' | 'directory'

type EoriResult = {
  eori: string
  companyName: string | null
  status: POAStatus | null
  found: boolean
}

type SyncImported  = { fileName: string; importerName: string; eori: string | null; warnings: ValidationWarning[] }
type SyncFailed    = { fileName: string; error: string }
type SyncDuplicate = { fileName: string; eori: string; existingCompany: string; existingAgent: string | null }
type SyncReport    = { imported: SyncImported[]; skipped: number; failed: SyncFailed[]; duplicates: SyncDuplicate[] }

function parseEoris(input: string): string[] {
  return [...new Set(
    input.split(/[\n,\t\s]+/)
      .map(s => s.trim())
      .filter(Boolean)
  )]
}

// Z73.6.1: same emoji badge scheme as eCustoms Preview / Job Detail / Customer Detail (see
// src/lib/poaStatus.ts) -- 'missing' isn't a per-row state here (every row obviously has a POA),
// it only shows up in the EORI-search panel's "not found" result below.
const STATUS_LABELS: Record<POAStatus, string> = {
  active: '🟢 ACTIVE',
  expiring_soon: '🟡 EXPIRING SOON',
  expired: '🔴 EXPIRED',
}

const STATUS_STYLES: Record<POAStatus, string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  expiring_soon: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
  expired: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
}

function DirectoryGroup({ agent, poas, navigate, eoriCounts }: {
  agent: string
  poas: POA[]
  navigate: any
  eoriCounts: Map<string, number>
}) {
  const [expanded, setExpanded] = useState(false)
  return (
    <div key={agent} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center justify-between transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className={`transition-transform ${expanded ? 'rotate-90' : ''}`}>▶</span>
          <span className="font-semibold text-gray-900 dark:text-white">{agent}</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
            {poas.length}
          </span>
        </div>
      </button>
      {expanded && (
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {poas.map(poa => (
            <div key={poa.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50">
              <div className="flex-1">
                <p className="font-medium text-gray-900 dark:text-white">{poa.importer_name}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  EORI: {poa.eori_number || '—'}
                  {poa.eori_number && (eoriCounts.get(poa.eori_number) ?? 0) > 1 && (
                    <span className="ml-2 inline-block px-1.5 py-0.5 bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300 rounded text-xs font-medium">DUP</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-3 ml-4">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[poa.status]}`}>
                  {STATUS_LABELS[poa.status]}
                </span>
                <button
                  onClick={() => navigate(`/customs/poa/${poa.id}`)}
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium transition-colors text-sm"
                >
                  View
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  })
}

const INPUT_CLS = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'
const LABEL_CLS = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'

export default function POAList() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<POAStatus | ''>('')
  const [agentFilter, setAgentFilter] = useState('')
  const [sortCol, setSortCol] = useState<SortCol>('created_at')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [showDeleted, setShowDeleted] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showEoriPanel, setShowEoriPanel] = useState(false)
  const [eoriInput, setEoriInput] = useState('')
  const [eoriResults, setEoriResults] = useState<EoriResult[] | null>(null)
  const [duplicateWarning, setDuplicateWarning] = useState<{ eori: string; existingName: string } | null>(null)

  function handleEoriSearch() {
    const eoris = parseEoris(eoriInput)
    const results: EoriResult[] = eoris.map(eori => {
      const match = poas.find(p => p.eori_number?.toLowerCase() === eori.toLowerCase())
      return match
        ? { eori, companyName: match.importer_name, status: match.status, found: true }
        : { eori, companyName: null, status: null, found: false }
    })
    setEoriResults(results)
  }

  function handleEoriClear() {
    setEoriInput('')
    setEoriResults(null)
  }

  const BLANK_FORM = {
    importer_name: '', eori_number: '', vat_number: '', signatory_name: '',
    importer_country: 'GB', importer_address: '', agent_name: '',
    signed_date: '', is_open_ended: false, valid_until: '',
    is_uk_local_company: false, is_pva: false, is_cds_approved: false, notes: '',
  }
  const [formData, setFormData] = useState(BLANK_FORM)
  const [formSubmitting, setFormSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  function resetForm() {
    setFormData(BLANK_FORM)
    setFormError(null)
  }

  const { userRoles, user } = useAuth()
  const userEmail = user?.email ?? ''
  const { poas, loading, error, stats, createPOA, softDeletePOA, hardDeletePOA, restorePOA, importFromDrive } = usePOAs()
  // Sync from Drive
  const [syncing, setSyncing] = useState(false)
  const [syncProgress, setSyncProgress] = useState('')
  const [syncReport, setSyncReport] = useState<SyncReport | null>(null)

  const handleSyncFromDrive = useCallback((accessToken: string) => {
    setSyncing(true)
    setSyncReport(null)
    setSyncProgress('Scanning Google Drive...')
    ;(async () => {
      try {
        const files = await scanPOAFolder(accessToken)
        setSyncProgress(`Found ${files.length} PDFs. Checking existing records...`)

        // Fetch all already-imported Drive file IDs in one query
        const { data: existing } = await supabase
          .from('poas')
          .select('drive_file_id')
          .not('drive_file_id', 'is', null)
        const existingIds = new Set((existing ?? []).map(r => r.drive_file_id as string))
        console.log(`[POA Sync] Checking ${files.length} files against ${existingIds.size} existing IDs`)

        // Partition into new vs already-imported
        let skipped = 0
        const toProcess = files.filter(f => {
          if (existingIds.has(f.fileId)) {
            console.log(`[POA Sync] Skipping already-imported: ${f.fileName} (ID: ${f.fileId}, agent: ${f.agentName})`)
            skipped++
            return false
          }
          return true
        })
        console.log(`[POA Sync] Processing ${toProcess.length} new files (skipped ${skipped})`)

        const imported: SyncImported[] = []
        const failed: SyncFailed[] = []
        const duplicates: SyncDuplicate[] = []

        for (const file of toProcess) {
          setSyncProgress(`Parsing ${file.fileName} (${imported.length + failed.length + duplicates.length + 1}/${toProcess.length} new)...`)
          try {
            const fileContent = await getDriveFileContent(file.fileId, file.mimeType, accessToken)
            const extracted = await parsePOAWithClaude({ ...fileContent, mimeType: file.mimeType }, file.agentName)
            const warnings = validateExtractedPOA(extracted)

            // Check if another record already has the same EORI (different drive file)
            const duplicate = extracted.eori_number
              ? poas.find(p => p.eori_number && p.eori_number === extracted.eori_number && p.drive_file_id !== file.fileId && !p.is_deleted)
              : undefined
            if (duplicate) {
              duplicates.push({
                fileName: file.fileName,
                eori: extracted.eori_number!,
                existingCompany: duplicate.importer_name,
                existingAgent: duplicate.agent_name,
              })
              continue
            }

            await importFromDrive(file, extracted)
            imported.push({ fileName: file.fileName, importerName: extracted.importer_name, eori: extracted.eori_number, warnings })
          } catch (e) {
            console.error('Failed to process', file.fileName, e)
            let errorMsg: string
            if (e instanceof Error) {
              errorMsg = e.message
              if (typeof errorMsg === 'object') {
                errorMsg = JSON.stringify(errorMsg)
              }
            } else if (typeof e === 'object' && e !== null) {
              errorMsg = (e as any).message || JSON.stringify(e)
            } else {
              errorMsg = String(e)
            }
            failed.push({ fileName: file.fileName, error: errorMsg })
          }
        }

        setSyncProgress('')
        setSyncReport({ imported, skipped, failed, duplicates })
      } catch (e) {
        setSyncProgress('Sync failed: ' + (e as Error).message)
        setTimeout(() => setSyncProgress(''), 5000)
      } finally {
        setSyncing(false)
      }
    })()
  }, [importFromDrive])

  // Redirect-based OAuth — avoids COOP popup issues
  function handleDriveLogin() {
    const params = new URLSearchParams({
      response_type: 'token',
      client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '',
      redirect_uri: window.location.origin + '/customs/poa',
      scope: 'https://www.googleapis.com/auth/drive.readonly',
    })
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`
  }

  // On return from OAuth redirect, access_token lands in the URL hash
  const [pendingDriveToken, setPendingDriveToken] = useState<string | null>(null)

  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.slice(1))
    const token = hash.get('access_token')
    if (token) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
      setPendingDriveToken(token)
    }
  }, [])

  useEffect(() => {
    if (pendingDriveToken) {
      handleSyncFromDrive(pendingDriveToken)
      setPendingDriveToken(null)
    }
  }, [pendingDriveToken, handleSyncFromDrive])

  async function handleCreatePOA(e: React.FormEvent) {
    e.preventDefault()
    setFormSubmitting(true)
    setFormError(null)
    setDuplicateWarning(null)

    if (duplicateWarning) {
      await createPOA({
        importer_name: formData.importer_name,
        eori_number: formData.eori_number || null,
        vat_number: formData.vat_number || null,
        signatory_name: formData.signatory_name || null,
        importer_country: formData.importer_country || null,
        importer_address: formData.importer_address || null,
        agent_name: formData.agent_name || null,
        signed_date: formData.signed_date || null,
        is_open_ended: formData.is_open_ended,
        valid_until: formData.is_open_ended ? null : (formData.valid_until || null),
        is_uk_local_company: formData.is_uk_local_company,
        is_pva: formData.is_pva,
        is_cds_approved: formData.is_cds_approved,
        notes: formData.notes || null,
        drive_file_id: null,
        drive_file_name: null,
        drive_web_link: null,
        ai_extracted: false,
        ai_confidence: null,
      })
      setIsModalOpen(false)
      resetForm()
      setDuplicateWarning(null)
      setFormSubmitting(false)
      return
    }

    if (formData.eori_number) {
      const existing = poas.find(p => p.eori_number === formData.eori_number && !p.is_deleted)
      if (existing) {
        setDuplicateWarning({ eori: formData.eori_number, existingName: existing.importer_name })
        setFormSubmitting(false)
        return
      }
    }

    try {
      await createPOA({
        importer_name: formData.importer_name,
        eori_number: formData.eori_number || null,
        vat_number: formData.vat_number || null,
        signatory_name: formData.signatory_name || null,
        importer_country: formData.importer_country || null,
        importer_address: formData.importer_address || null,
        agent_name: formData.agent_name || null,
        signed_date: formData.signed_date || null,
        is_open_ended: formData.is_open_ended,
        valid_until: formData.is_open_ended ? null : (formData.valid_until || null),
        is_uk_local_company: formData.is_uk_local_company,
        is_pva: formData.is_pva,
        is_cds_approved: formData.is_cds_approved,
        notes: formData.notes || null,
        drive_file_id: null,
        drive_file_name: null,
        drive_web_link: null,
        ai_extracted: false,
        ai_confidence: null,
      })
      setIsModalOpen(false)
      resetForm()
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create POA')
    } finally {
      setFormSubmitting(false)
    }
  }


  const activeFilterCount = [search, statusFilter, agentFilter, showDeleted].filter(f => f).length

  const eoriCounts = new Map<string, number>()
  poas.forEach(p => p.eori_number && !p.is_deleted && eoriCounts.set(p.eori_number, (eoriCounts.get(p.eori_number) ?? 0) + 1))

  const filtered = poas
    .filter(p => !p.is_deleted || showDeleted)
    .filter(p => !statusFilter || p.status === statusFilter)
    .filter(p => !agentFilter || p.agent_name === agentFilter)
    .filter(p => {
      if (!search.trim()) return true
      const q = search.toLowerCase()
      return p.importer_name.toLowerCase().includes(q) ||
             p.eori_number?.toLowerCase().includes(q) ||
             p.agent_name?.toLowerCase().includes(q)
    })
    .sort((a, b) => {
      let aVal: any = ''
      let bVal: any = ''
      if (sortCol === 'importer_name') { aVal = a.importer_name; bVal = b.importer_name }
      if (sortCol === 'eori') { aVal = a.eori_number; bVal = b.eori_number }
      if (sortCol === 'agent_name') { aVal = a.agent_name; bVal = b.agent_name }
      if (sortCol === 'signed_date') { aVal = a.signed_date; bVal = b.signed_date }
      if (sortCol === 'created_at') { aVal = a.created_at; bVal = b.created_at }

      if (aVal == null) aVal = ''
      if (bVal == null) bVal = ''

      const isAsc = sortDir === 'asc'
      if (aVal < bVal) return isAsc ? -1 : 1
      if (aVal > bVal) return isAsc ? 1 : -1
      return 0
    })

  const groupedByAgent = new Map<string, POA[]>()
  filtered.forEach(p => {
    const agent = p.agent_name || '(No Agent)'
    if (!groupedByAgent.has(agent)) groupedByAgent.set(agent, [])
    groupedByAgent.get(agent)!.push(p)
  })

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <button
                onClick={() => navigate('/customs')}
                className="hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                Customs
              </button>
              <span>/</span>
              <span className="text-gray-900 dark:text-white">POA Management</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Power of Attorney
            </h1>
          </div>
          {syncProgress && (
            <span className="text-sm text-blue-600 dark:text-blue-400 mr-3">{syncProgress}</span>
          )}
          <button
            onClick={handleDriveLogin}
            disabled={syncing}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors mr-2"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
            </svg>
            {syncing ? 'Syncing...' : 'Sync from Drive'}
          </button>
          <button
            onClick={() => { setShowEoriPanel(p => !p); handleEoriClear() }}
            className="inline-flex items-center px-4 py-2 border border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors mr-2"
          >
            Batch EORI Search
          </button>
          <button
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New POA
          </button>
        </div>

        {/* Z63.2: POA expiry alerts, moved here from the Dashboard -- staff sees them when
            they open this module rather than as a standing banner on the app home page. */}
        {stats.expired > 0 && (
          <button
            onClick={() => setStatusFilter('expired')}
            className="w-full mb-4 flex items-center justify-between px-4 py-3 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg text-sm text-red-800 dark:text-red-300 text-left"
          >
            <span>🔴 <strong>{stats.expired}</strong> POA{stats.expired !== 1 ? 's' : ''} have expired</span>
            <span className="font-semibold underline ml-4 whitespace-nowrap">View</span>
          </button>
        )}
        {stats.expiring_soon > 0 && (
          <button
            onClick={() => setStatusFilter('expiring_soon')}
            className="w-full mb-4 flex items-center justify-between px-4 py-3 bg-orange-50 dark:bg-orange-900/30 border border-orange-300 dark:border-orange-700 rounded-lg text-sm text-orange-800 dark:text-orange-300 text-left"
          >
            <span>⚠️ <strong>{stats.expiring_soon}</strong> POA{stats.expiring_soon !== 1 ? 's' : ''} expiring within 30 days</span>
            <span className="font-semibold underline ml-4 whitespace-nowrap">View</span>
          </button>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {(['active', 'expiring_soon', 'expired'] as POAStatus[]).map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
              className={`p-4 rounded-lg border text-left transition-all ${
                statusFilter === s
                  ? 'ring-2 ring-blue-500 border-blue-300 dark:border-blue-600'
                  : 'border-gray-200 dark:border-gray-700'
              } bg-white dark:bg-gray-800 shadow-sm`}
            >
              <p className="text-sm text-gray-500 dark:text-gray-400">{STATUS_LABELS[s]}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {loading ? '...' : stats[s]}
              </p>
              <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[s]}`}>
                {STATUS_LABELS[s]}
              </span>
            </button>
          ))}
        </div>

        {/* Batch EORI Search Panel */}
        {showEoriPanel && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm mb-4 p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Batch EORI Search</h2>
              <button
                onClick={() => { setShowEoriPanel(false); handleEoriClear() }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                aria-label="Close"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <textarea
              value={eoriInput}
              onChange={e => setEoriInput(e.target.value)}
              placeholder="Paste EORI numbers here, one per line or comma-separated"
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-y mb-3"
            />
            <div className="flex gap-2 mb-4">
              <button
                onClick={handleEoriSearch}
                disabled={!eoriInput.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm rounded-md transition-colors"
              >
                Search
              </button>
              <button
                onClick={handleEoriClear}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Clear
              </button>
            </div>

            {eoriResults && (() => {
              const found = eoriResults.filter(r => r.found).length
              const notFound = eoriResults.length - found
              return (
                <>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    <span className="text-green-600 dark:text-green-400 font-medium">{found} found</span>
                    {', '}
                    <span className="text-red-600 dark:text-red-400 font-medium">{notFound} not found</span>
                    {` out of ${eoriResults.length} searched`}
                  </p>
                  <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-600">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">EORI</th>
                          <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">Company Name</th>
                          <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">Status</th>
                          <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">Result</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {eoriResults.map(r => (
                          <tr
                            key={r.eori}
                            className={r.found ? '' : 'bg-red-50 dark:bg-red-900/20'}
                          >
                            <td className="px-3 py-2 font-mono text-gray-900 dark:text-white">{r.eori}</td>
                            <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{r.companyName ?? '—'}</td>
                            <td className="px-3 py-2">
                              {r.status ? (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[r.status]}`}>
                                  {STATUS_LABELS[r.status]}
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                  ⚫ MISSING
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {r.found
                                ? <span className="text-green-600 dark:text-green-400 font-medium">✓ Found</span>
                                : <span className="text-red-600 dark:text-red-400 font-medium">✗ Not Found</span>
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )
            })()}
          </div>
        )}

        {/* Search & Filter */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm mb-4 p-4">
          <div className="flex flex-col gap-3">
            <div className="flex gap-3 items-center flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by name, EORI, or agent..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              <select
                value={agentFilter || ''}
                onChange={e => setAgentFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">All Agents</option>
                {Array.from(new Set(poas.map(p => p.agent_name).filter(Boolean))).sort().map(agent => (
                  <option key={agent || ''} value={agent || ''}>{agent}</option>
                ))}
              </select>
              <select
                value={statusFilter || ''}
                onChange={e => setStatusFilter(e.target.value as POAStatus | '')}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="expiring_soon">Expiring Soon</option>
                <option value="expired">Expired</option>
              </select>
              <button
                onClick={() => setViewMode(viewMode === 'list' ? 'directory' : 'list')}
                className="p-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                title={viewMode === 'list' ? 'Switch to directory view' : 'Switch to list view'}
              >
                {viewMode === 'list' ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                  {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="flex gap-3 items-center flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showDeleted}
                  onChange={e => setShowDeleted(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Show deleted</span>
              </label>
              {(search || statusFilter || agentFilter || showDeleted) && (
                <button
                  onClick={() => { setSearch(''); setStatusFilter(''); setAgentFilter(''); setShowDeleted(false) }}
                  className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-sm text-red-800 dark:text-red-300">
            {error}
          </div>
        )}

        {/* Table or Directory View */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          {viewMode === 'list' ? (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" onClick={() => { setSortCol('importer_name'); setSortDir(sortCol === 'importer_name' && sortDir === 'desc' ? 'asc' : 'desc') }}>
                    Importer Name {sortCol === 'importer_name' && <span className="text-xs ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" onClick={() => { setSortCol('agent_name'); setSortDir(sortCol === 'agent_name' && sortDir === 'desc' ? 'asc' : 'desc') }}>
                    Agent {sortCol === 'agent_name' && <span className="text-xs ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Local</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" onClick={() => { setSortCol('eori'); setSortDir(sortCol === 'eori' && sortDir === 'desc' ? 'asc' : 'desc') }}>
                    EORI Number {sortCol === 'eori' && <span className="text-xs ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" onClick={() => { setSortCol('signed_date'); setSortDir(sortCol === 'signed_date' && sortDir === 'desc' ? 'asc' : 'desc') }}>
                    Signed Date {sortCol === 'signed_date' && <span className="text-xs ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Valid Until</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" onClick={() => { setSortCol('created_at'); setSortDir(sortCol === 'created_at' && sortDir === 'desc' ? 'asc' : 'desc') }}>
                    Imported {sortCol === 'created_at' && <span className="text-xs ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-8 text-center text-gray-400 dark:text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                      No POAs found.
                    </td>
                  </tr>
                ) : (
                  filtered.map(poa => (
                    <tr
                      key={poa.id}
                      className={`transition-colors ${
                        poa.is_deleted
                          ? 'opacity-50 bg-gray-100 dark:bg-gray-800 hover:bg-gray-150 dark:hover:bg-gray-750'
                          : poa.status === 'expired'
                          ? 'border-l-4 border-red-400 bg-red-50 dark:bg-red-900/10 hover:bg-red-100 dark:hover:bg-red-900/20'
                          : poa.status === 'expiring_soon'
                          ? 'border-l-4 border-orange-400 bg-orange-50 dark:bg-orange-900/10 hover:bg-orange-100 dark:hover:bg-orange-900/20'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                      title={poa.is_deleted ? `Deleted by ${poa.deleted_by_email} on ${formatDate(poa.deleted_at)}` : ''}
                    >
                      <td className={`px-4 py-3 font-medium text-gray-900 dark:text-white ${poa.is_deleted ? 'line-through' : ''}`}>
                        {poa.importer_name}
                      </td>
                      <td className={`px-4 py-3 text-gray-600 dark:text-gray-300 ${poa.is_deleted ? 'line-through' : ''}`}>
                        {poa.agent_name || '—'}
                      </td>
                      <td className="px-4 py-3">
                        {poa.is_uk_local_company
                          ? <span className="inline-block px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 rounded text-xs font-medium">🇬🇧 Local</span>
                          : <span className="text-gray-400">—</span>
                        }
                      </td>
                      <td className={`px-4 py-3 text-gray-600 dark:text-gray-300 ${poa.is_deleted ? 'line-through' : ''}`}>
                        <div className="flex items-center gap-2">
                          <span>{poa.eori_number || '—'}</span>
                          {poa.eori_number && (eoriCounts.get(poa.eori_number) ?? 0) > 1 && (
                            <span className="inline-block px-1.5 py-0.5 bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300 rounded text-xs font-medium">DUP</span>
                          )}
                        </div>
                      </td>
                      <td className={`px-4 py-3 text-gray-600 dark:text-gray-300 ${poa.is_deleted ? 'line-through' : ''}`}>
                        {formatDate(poa.signed_date)}
                      </td>
                      <td className={`px-4 py-3 text-gray-600 dark:text-gray-300 ${poa.is_deleted ? 'line-through' : ''}`}>
                        {poa.valid_until_estimated
                          ? <span className="italic text-gray-500 dark:text-gray-400 text-xs">Est. {formatDate(poa.valid_until)}</span>
                          : formatDate(poa.valid_until)}
                      </td>
                      <td className={`px-4 py-3 text-gray-600 dark:text-gray-300 ${poa.is_deleted ? 'line-through' : ''}`}>
                        {new Date(poa.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[poa.status]}`}>
                          {STATUS_LABELS[poa.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => navigate(`/customs/poa/${poa.id}`)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium transition-colors"
                          >
                            View
                          </button>
                          {poa.is_deleted ? (
                            <>
                              {userRoles.includes('ADMIN') && (
                                <>
                                  <button
                                    onClick={async () => {
                                      const confirmed = window.confirm('Restore this POA?')
                                      if (confirmed) await restorePOA(poa.id)
                                    }}
                                    className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 font-medium transition-colors text-sm"
                                  >
                                    Restore
                                  </button>
                                  {/* Z72: a Drive-imported POA can't be permanently deleted -- that
                                      would destroy the drive_file_id fingerprint that keeps the next
                                      "Sync from Drive" from re-importing this exact file. Soft-deleted
                                      is as permanent as this file can be while it still exists in Drive. */}
                                  {poa.drive_file_id ? (
                                    <span
                                      className="text-gray-400 dark:text-gray-500 text-sm cursor-default"
                                      title="Imported from Google Drive -- permanently deleting would let the next sync re-import this same file. Delete it from Drive itself if you need it fully gone."
                                    >
                                      Delete (Drive-imported)
                                    </span>
                                  ) : (
                                    <button
                                      onClick={async () => {
                                        const confirmed = window.confirm('Permanently delete this POA? This cannot be undone.')
                                        if (!confirmed) return
                                        try {
                                          await hardDeletePOA(poa.id)
                                        } catch (e) {
                                          window.alert(e instanceof Error ? e.message : 'Failed to permanently delete POA')
                                        }
                                      }}
                                      className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 font-medium transition-colors text-sm"
                                    >
                                      Delete
                                    </button>
                                  )}
                                </>
                              )}
                            </>
                          ) : (
                            <button
                              onClick={async () => {
                                const confirmed = window.confirm(
                                  `Delete this POA for ${poa.importer_name}?`
                                )
                                if (confirmed) await softDeletePOA(poa.id, userEmail)
                              }}
                              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 font-medium transition-colors"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : (
            <div className="p-4">
              {loading ? (
                <p className="text-center text-gray-400 dark:text-gray-500 py-8">Loading...</p>
              ) : filtered.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">No POAs found.</p>
              ) : (
                <div className="space-y-2">
                  {Array.from(groupedByAgent.entries()).map(([agent, poasInGroup]) => (
                    <DirectoryGroup key={agent} agent={agent} poas={poasInGroup} navigate={navigate} eoriCounts={eoriCounts} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
          Showing {filtered.length} of {stats.total} records
        </p>
      </div>

      {/* New POA Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">New POA</h2>
              <button
                type="button"
                onClick={() => { setIsModalOpen(false); resetForm(); setDuplicateWarning(null) }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Duplicate Warning Overlay */}
            {duplicateWarning && (
              <div className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center z-10">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm shadow-2xl">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Duplicate EORI Found</h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                    A POA for EORI <strong>{duplicateWarning.eori}</strong> already exists (<strong>{duplicateWarning.existingName}</strong>).
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">Do you want to continue anyway?</p>
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => setDuplicateWarning(null)}
                      className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleCreatePOA(new Event('submit') as any)}
                      disabled={formSubmitting}
                      className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md transition-colors"
                    >
                      {formSubmitting ? 'Saving...' : 'Save Anyway'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Scrollable form body */}
            <form onSubmit={handleCreatePOA} className="flex flex-col flex-1 overflow-hidden">
              <div className="overflow-y-auto flex-1 px-6 py-4">
                <div className="grid grid-cols-2 gap-4">

                  {/* Importer Name — full width, required */}
                  <div className="col-span-2">
                    <label className={LABEL_CLS}>Importer Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      required
                      value={formData.importer_name}
                      onChange={e => setFormData(f => ({ ...f, importer_name: e.target.value }))}
                      className={INPUT_CLS}
                      placeholder="e.g. Acme Imports Ltd"
                    />
                  </div>

                  {/* EORI */}
                  <div>
                    <label className={LABEL_CLS}>EORI Number</label>
                    <input type="text" value={formData.eori_number}
                      onChange={e => setFormData(f => ({ ...f, eori_number: e.target.value }))}
                      className={INPUT_CLS} placeholder="e.g. GB123456789000" />
                  </div>

                  {/* VAT */}
                  <div>
                    <label className={LABEL_CLS}>VAT Number</label>
                    <input type="text" value={formData.vat_number}
                      onChange={e => setFormData(f => ({ ...f, vat_number: e.target.value }))}
                      className={INPUT_CLS} placeholder="e.g. GB123456789" />
                  </div>

                  {/* Signatory Name */}
                  <div>
                    <label className={LABEL_CLS}>Signatory Name</label>
                    <input type="text" value={formData.signatory_name}
                      onChange={e => setFormData(f => ({ ...f, signatory_name: e.target.value }))}
                      className={INPUT_CLS} placeholder="Full name of signatory" />
                  </div>

                  {/* Country */}
                  <div>
                    <label className={LABEL_CLS}>Country</label>
                    <input type="text" value={formData.importer_country}
                      onChange={e => setFormData(f => ({ ...f, importer_country: e.target.value }))}
                      className={INPUT_CLS} placeholder="GB" />
                  </div>

                  {/* Importer Address — full width */}
                  <div className="col-span-2">
                    <label className={LABEL_CLS}>Importer Address</label>
                    <textarea value={formData.importer_address}
                      onChange={e => setFormData(f => ({ ...f, importer_address: e.target.value }))}
                      rows={2} className={INPUT_CLS + ' resize-none'}
                      placeholder="Street, City, Postcode" />
                  </div>

                  {/* Agent Name — full width */}
                  <div className="col-span-2">
                    <label className={LABEL_CLS}>Agent Name</label>
                    <input type="text" value={formData.agent_name}
                      onChange={e => setFormData(f => ({ ...f, agent_name: e.target.value }))}
                      className={INPUT_CLS} placeholder="Customs agent / broker name" />
                  </div>

                  {/* Signed Date */}
                  <div>
                    <label className={LABEL_CLS}>Signed Date</label>
                    <input type="date" value={formData.signed_date}
                      onChange={e => setFormData(f => ({ ...f, signed_date: e.target.value }))}
                      className={INPUT_CLS} />
                  </div>

                  {/* Valid Until — disabled when open-ended */}
                  <div>
                    <label className={LABEL_CLS + (formData.is_open_ended ? ' opacity-40' : '')}>Valid Until</label>
                    <input type="date" value={formData.valid_until}
                      disabled={formData.is_open_ended}
                      onChange={e => setFormData(f => ({ ...f, valid_until: e.target.value }))}
                      className={INPUT_CLS + (formData.is_open_ended ? ' opacity-40 cursor-not-allowed' : '')} />
                  </div>

                  {/* Checkboxes — 2×2 grid */}
                  <div className="col-span-2 grid grid-cols-2 gap-y-3">
                    {([
                      { key: 'is_open_ended',       label: 'Open-ended (no expiry)' },
                      { key: 'is_uk_local_company',  label: 'UK Local Company' },
                      { key: 'is_pva',               label: 'PVA Authorisation' },
                      { key: 'is_cds_approved',      label: 'CDS Approved' },
                    ] as const).map(({ key, label }) => (
                      <label key={key} className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={formData[key]}
                          onChange={e => setFormData(f => ({ ...f, [key]: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                      </label>
                    ))}
                  </div>

                  {/* Notes — full width */}
                  <div className="col-span-2">
                    <label className={LABEL_CLS}>Notes</label>
                    <textarea value={formData.notes}
                      onChange={e => setFormData(f => ({ ...f, notes: e.target.value }))}
                      rows={3} className={INPUT_CLS + ' resize-y'}
                      placeholder="Additional notes..." />
                  </div>
                </div>
              </div>

              {/* Error */}
              {formError && (
                <div className="mx-6 mb-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900 rounded text-sm text-red-700 dark:text-red-400 shrink-0">
                  {formError}
                </div>
              )}

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 shrink-0">
                <button type="button"
                  onClick={() => { setIsModalOpen(false); resetForm() }}
                  className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button type="submit"
                  disabled={formSubmitting}
                  className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md transition-colors"
                >
                  {formSubmitting ? 'Saving...' : 'Create POA'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Sync Report Modal */}
      {syncReport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Sync Complete</h2>
              <button
                onClick={() => setSyncReport(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="overflow-y-auto flex-1 px-6 py-4 space-y-5">
              {/* Imported */}
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  ✅ {syncReport.imported.length} new POA{syncReport.imported.length !== 1 ? 's' : ''} imported
                </p>
                {syncReport.imported.length > 0 ? (
                  <div className="rounded-md border border-gray-200 dark:border-gray-600 overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">File Name</th>
                          <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">Importer Name</th>
                          <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">EORI</th>
                          <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">Warnings</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {syncReport.imported.map((r, i) => (
                          <tr key={i} className={r.warnings.length > 0 ? 'bg-yellow-50 dark:bg-yellow-900/10' : 'bg-green-50 dark:bg-green-900/10'}>
                            <td className="px-3 py-2 text-gray-700 dark:text-gray-300 font-mono truncate max-w-[180px]">{r.fileName}</td>
                            <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{r.importerName}</td>
                            <td className="px-3 py-2 text-gray-500 dark:text-gray-400 font-mono">{r.eori ?? '—'}</td>
                            <td className="px-3 py-2">
                              {r.warnings.length > 0 ? (
                                <div className="flex flex-col gap-1">
                                  {r.warnings.map((w, j) => (
                                    <span key={j} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300">
                                      ⚠️ {w.field}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-green-600 dark:text-green-400">✓</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400 italic">No new files were imported.</p>
                )}
              </div>

              {/* Skipped */}
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <span>⏭️</span>
                <span>
                  <strong className="text-gray-900 dark:text-white">{syncReport.skipped}</strong>
                  {' '}file{syncReport.skipped !== 1 ? 's' : ''} skipped (already exist in database)
                </span>
              </div>

              {/* Duplicates */}
              {syncReport.duplicates.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                    ⚠️ {syncReport.duplicates.length} possible duplicate{syncReport.duplicates.length !== 1 ? 's' : ''} found (same EORI already exists)
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-400 mb-2 italic">
                    These files were not imported. Review manually and delete the old record if needed.
                  </p>
                  <div className="rounded-md border border-amber-200 dark:border-amber-700 overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-700">
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold text-amber-800 dark:text-amber-300">File Name</th>
                          <th className="text-left px-3 py-2 font-semibold text-amber-800 dark:text-amber-300">EORI</th>
                          <th className="text-left px-3 py-2 font-semibold text-amber-800 dark:text-amber-300">Existing Company</th>
                          <th className="text-left px-3 py-2 font-semibold text-amber-800 dark:text-amber-300">Existing Agent</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-amber-100 dark:divide-amber-900">
                        {syncReport.duplicates.map((r, i) => (
                          <tr key={i} className="bg-amber-50/50 dark:bg-amber-900/10">
                            <td className="px-3 py-2 text-gray-700 dark:text-gray-300 font-mono truncate max-w-[160px]">{r.fileName}</td>
                            <td className="px-3 py-2 text-gray-700 dark:text-gray-300 font-mono">{r.eori}</td>
                            <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{r.existingCompany}</td>
                            <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{r.existingAgent ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Failed */}
              {syncReport.failed.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    ❌ {syncReport.failed.length} file{syncReport.failed.length !== 1 ? 's' : ''} failed
                  </p>
                  <div className="rounded-md border border-red-200 dark:border-red-800 overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-red-50 dark:bg-red-900/30 border-b border-red-200 dark:border-red-800">
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold text-red-700 dark:text-red-300">File Name</th>
                          <th className="text-left px-3 py-2 font-semibold text-red-700 dark:text-red-300">Error</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-red-100 dark:divide-red-900">
                        {syncReport.failed.map((r, i) => (
                          <tr key={i} className="bg-red-50 dark:bg-red-900/10">
                            <td className="px-3 py-2 text-gray-700 dark:text-gray-300 font-mono truncate max-w-[180px]">{r.fileName}</td>
                            <td className="px-3 py-2 text-red-700 dark:text-red-400">{r.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end shrink-0">
              <button
                onClick={() => setSyncReport(null)}
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
